import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Button, Card, Spinner } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import md5 from 'md5';
import RichTextEditor from '../../../components/RichTextEditor';
import { getPrefix } from '../../../util/utils';
import api from '../../../services/api';
import history from '../../../services/history';
import { requestLaudoPresignDownload } from '../../../services/laudoFiles';
import LaudoCardHeader from '../../../components/laudos/LaudoCardHeader';
import useEcg from '../../../hooks/useEcg';

const isRequestAborted = err =>
  err?.name === 'AbortError' ||
  err?.name === 'CanceledError' ||
  err?.code === 'ERR_CANCELED';

export default function ReportEcg() {
  const { id } = useParams();
  const { data, error, isLoading } = useEcg(id);
  const laudo = data?.[0];
  const [image, setImage] = useState({ cod: null, url: null });
  const [loadingImage, setLoadingImage] = useState(false);
  const url = image.cod === laudo?.CODECG ? image.url : null;
  const contentFooter = !laudo
    ? ''
    : `<u>Autenticação</u> -
    <strong className="mr-auto">
      ${getPrefix(laudo.CODREALIZOU)} ${laudo.Realizou}
    </strong>
    <i className="mr-auto">
      - ${md5(JSON.stringify(laudo))}
    </i>`;

  useEffect(() => {
    const codEcg = laudo?.CODECG;
    if (!codEcg) {
      setLoadingImage(false);
      return undefined;
    }

    const abortController = new AbortController();
    const { signal } = abortController;

    const loadImage = async () => {
      let imageFile;

      try {
        const { data: filesData } = await api.get('/files', {
          params: { codLaudo: codEcg, procedimento: 'ecg' },
          signal,
        });

        if (signal.aborted) return;

        [imageFile] = filesData.filter(
          item =>
            item.Key.includes('.jpg') ||
            item.Key.includes('.bmp') ||
            item.Key.includes('.BMP')
        );
      } catch (err) {
        if (isRequestAborted(err) || signal.aborted) return;
        toast.error(err.response.data.error);
        return;
      }

      if (!imageFile) {
        setImage({ cod: codEcg, url: null });
        setLoadingImage(false);
        return;
      }

      try {
        setLoadingImage(true);
        const { data: presign } = await requestLaudoPresignDownload(
          { key: imageFile.Key },
          { signal }
        );

        if (signal.aborted) return;
        setImage({ cod: codEcg, url: presign?.preSignedUrl });
      } catch (err) {
        if (isRequestAborted(err) || signal.aborted) return;
        toast.error('Erro ao carregar o arquivo.');
      } finally {
        if (!signal.aborted) setLoadingImage(false);
      }
    };

    loadImage();

    return () => {
      abortController.abort();
    };
  }, [data, laudo]);

  return (
    <Card style={{ marginTop: 10 }}>
      <LaudoCardHeader title="ELETROCARDIOGRAMA" />

      {isLoading && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '80vh',
          }}
        >
          <Spinner animation="border" variant="dark" size="lg" />
        </div>
      )}

      {!isLoading && (error || !laudo) && (
        <>
          <span
            className="text-danger text-center d-block"
            style={{ marginTop: 10 }}
          >
            {error
              ? 'Erro ao carregar laudo de eletrocardiograma! Tente novamente.'
              : 'Laudo não encontrado.'}
          </span>
          <Card.Footer className="text-muted text-center">
            <Button variant="warning" onClick={() => history.goBack()}>
              Voltar
            </Button>
          </Card.Footer>
        </>
      )}

      {!isLoading && !error && laudo && (
        <Card.Body>
          {loadingImage ? (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Spinner animation="border" />
            </div>
          ) : (
            <img alt="laudo" loading="lazy" src={url} width="100%" height="80%" />
          )}

          <RichTextEditor
            content={laudo.OBSERVACAO}
            contentFooter={contentFooter}
            imageUrl={url}
            readOnly
          />

          <Card.Footer className="mt-3 text-center">
            <u>Autenticação</u> -{' '}
            <strong className="mr-auto">
              {getPrefix(laudo.CODREALIZOU)} {laudo.Realizou}
            </strong>{' '}
            -{' '}
            <i className="mr-auto">
              {md5(JSON.stringify(`${laudo.CODPAC} - ${laudo.CODECG}`))}
            </i>
          </Card.Footer>
        </Card.Body>
      )}
    </Card>
  );
}
